import { type ReactNode } from "react";

import { useAnalytics } from "@/providers";
import type { FilterDefinition } from "@/shared/components/filters/FilterDefinition";
import type { Filter } from "@/shared/components/filters/FilterSchema";
import { usePopupState } from "@/shared/hooks/usePopupState";

import { EditFilterMenu } from "../EditFilterMenu";

import { FilterChip } from "./FilterChip";

export interface EditableFilterChipProps {
  /** The committed filter this chip represents. */
  filter: Filter;
  /** This filter's position in the structured list — echoed back to
   *  `onUpdate`/`onRemove` so the parent can target the right row. */
  index: number;
  /** Human label for `filter.field`, resolved by the parent from the
   *  definition list (falls back to the raw field for unmapped filters). */
  fieldName: string;
  /** Definition backing `filter.field`, threaded into the edit form for its
   *  header and per-field comparator/options metadata. Absent for metric
   *  filters (the metric form self-derives) and for unmapped filters — see
   *  `editable`. */
  definition?: FilterDefinition;
  /** Whether the chip body opens an edit popover. False for a property
   *  filter whose definition didn't resolve (no form metadata to edit
   *  with); the chip still renders and removes. */
  editable: boolean;
  onRemove: (index: number) => void;
  onUpdate: (index: number, filter: Filter) => void;
  /** Controls the caller adds to the chip (`FilterChip`'s `actions`). */
  actions?: ReactNode;
}

/**
 * A `<FilterChip>` paired with its own edit popover — a self-contained unit
 * that owns its open state and anchors the popover to its own body. Because
 * `filter` is a prop that's always in scope (never a transient "which chip
 * is selected" that clears on close), the popover needs no content-retention
 * dance: during MUI's close transition `filter` is still this chip's value,
 * so the form stays rendered through the fade.
 *
 * This is the trigger-per-chip shape a future compound `<Popover>` primitive
 * will formalize; today it composes the existing `<FilterChip>`,
 * `<EditFilterMenu>`, and `usePopupState` directly.
 */
export function EditableFilterChip({
  filter,
  index,
  fieldName,
  definition,
  editable,
  onRemove,
  onUpdate,
  actions,
}: EditableFilterChipProps) {
  const popup = usePopupState();
  const { trackEvent } = useAnalytics();

  return (
    <>
      <FilterChip
        filter={filter}
        fieldName={fieldName}
        actions={actions}
        onRemove={() => onRemove(index)}
        onEdit={
          editable
            ? (event) => {
                trackEvent("EditFilterClicked", {
                  filterType: filter.type,
                  comparator: filter.comparator,
                });
                popup.open({ anchorEl: event.currentTarget });
              }
            : undefined
        }
      />
      <EditFilterMenu
        open={popup.isOpen}
        anchorEl={popup.anchor}
        onClose={popup.close}
        filter={filter}
        definition={definition}
        onCommit={(next) => onUpdate(index, next)}
      />
    </>
  );
}
